import { useQuery } from '@tanstack/react-query';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import { APITypes } from '../../primary-record-types';
import { useState } from 'react';
import { getProfileRefreshProgressCacheKey } from './cacheKeys';
import { useResetConnectedHealthSystems } from './useProfileConnectedHealthSystems';
import useQueryCallbacks from '../useQueryCallbacks';
import { useHasPermission } from '../../components/v2BaseComponents/Permissions/hooks';

const intl = getIntl('useMedicalProfilesRefreshProgress', {
    errorFetchingRefreshProgress: 'Error loading refresh progress',
});

export type UseMedicalProfileRefreshProgressArgs = {
    medicalProfileId: string;
};

export type UseMedicalProfileRefreshProgressReturnValue = {
    progress: APITypes.ehrIntegrationsService.RefreshProgressResponse | null;
    refetchProgress: () => void;
    isRefreshing: boolean;
};

const POLLING_INTERVAL_SECONDS = 2;

export default function useOneUpHealthRefreshProgress({
    medicalProfileId,
}: UseMedicalProfileRefreshProgressArgs): UseMedicalProfileRefreshProgressReturnValue {
    const notify = useNotify();
    const hasPermission = useHasPermission(medicalProfileId, 'patientPortals', 'view');
    const resetConnectedHealthSystems = useResetConnectedHealthSystems();
    const [latestRefreshProgress, setLatestRefreshProgress] =
        useState<APITypes.ehrIntegrationsService.RefreshProgressResponse>({ status: 'no-progress-record-found' });
    const refetchInterval =
        latestRefreshProgress.status === 'progress-record-found' &&
        latestRefreshProgress.progress.status === 'in-progress' &&
        !latestRefreshProgress.isRefreshExpired
            ? POLLING_INTERVAL_SECONDS * 1000
            : POLLING_INTERVAL_SECONDS * 10 * 1000;
    const progressQuery = useQuery({
        queryKey: getProfileRefreshProgressCacheKey(medicalProfileId),

        queryFn: async () => {
            return appAPI.get<APITypes.ehrIntegrationsService.RefreshProgressResponse>(
                urls.api.ehrIntegrations.oneUpHealth.endpoints.refreshProgress(medicalProfileId),
            );
        },
        refetchInterval,
        initialData: latestRefreshProgress,
        enabled: hasPermission,
    });
    useQueryCallbacks({
        query: progressQuery,
        onError: () => {
            notify('error', intl('errorFetchingRefreshProgress'));
        },
        onSuccess: (response) => {
            if (response.status === 'progress-record-found') {
                resetConnectedHealthSystems(medicalProfileId);
            }

            setLatestRefreshProgress(response);
        },
    });

    return {
        progress: progressQuery.data || null,
        refetchProgress: progressQuery.refetch,
        isRefreshing:
            progressQuery.isLoading ||
            (progressQuery.data?.status === 'progress-record-found' &&
                progressQuery.data?.progress?.status === 'in-progress' &&
                !progressQuery.data?.isRefreshExpired),
    };
}
